import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import axios from 'axios';

interface SearchResult {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
}

interface UtmLocationSearchProps {
  onLocationSelect: (lat: number, lon: number, name: string) => void;
  leftOffsetPx?: number;
}

const UtmLocationSearch: React.FC<UtmLocationSearchProps> = ({
  onLocationSelect,
  leftOffsetPx = 16,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      setShowResults(false);
      return;
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await axios.get<SearchResult[]>(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
        );
        setResults(response.data);
        setShowResults(true);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsLoading(false);
      }
    }, 500);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query]);

  const handleSelect = (result: SearchResult) => {
    onLocationSelect(parseFloat(result.lat), parseFloat(result.lon), result.display_name);
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <div
      className="absolute z-20 w-[calc(100vw-1rem)] max-w-[22rem] sm:top-4 sm:w-80"
      style={isMobile ? { top: 56, left: 8 } : { top: 16, left: leftOffsetPx }}
    >
      <div className="relative">
        <div className="flex items-center bg-card border border-border rounded-lg shadow-xl">
          <Search className="absolute left-3 text-muted-foreground" size={18} />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search for a location..."
            className="w-full bg-transparent border-none text-foreground text-sm pl-10 pr-10 py-2 focus:outline-none placeholder:text-muted-foreground"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setResults([]);
                setShowResults(false);
              }}
              className="absolute right-3 text-muted-foreground hover:text-accent-foreground"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {showResults && results.length > 0 && (
          <div className="absolute top-full mt-1 w-full bg-card border border-border rounded-lg shadow-xl overflow-hidden max-h-64 overflow-y-auto">
            {results.map((result) => (
              <button
                type="button"
                key={result.place_id}
                onClick={() => handleSelect(result)}
                className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-accent transition-colors border-b border-border last:border-b-0"
              >
                {result.display_name}
              </button>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="absolute top-full mt-1 w-full bg-card border border-border rounded-lg shadow-xl px-4 py-2 text-sm text-muted-foreground text-center">
            Searching...
          </div>
        )}
      </div>
    </div>
  );
};

export default UtmLocationSearch;
